// src/utils/lessonHandoff.js
//
// Pass 66 — BOOKING → LESSONINSTANCE HANDOFF. A canonical Booking (what was sold/scheduled) and a
// lessonInstance (what is actually delivered) are DISTINCT objects. This module hands a legitimate,
// assigned, current client booking off to the existing lesson-delivery surface by lazily creating ONE
// deterministic, booking-linked lessonInstance (get-or-create) — never at booking confirmation, never on
// render, and never fabricating a student/Auth identity from the commercial clientId.
//
// Canonical write target = TOP-LEVEL `lessonInstances` (the collection both delivery surfaces already use;
// the org-scoped `orgs/{orgId}/lessonInstances` is a legacy read path with no current writer — no dual-write).

import { runTransaction, doc, serverTimestamp } from 'firebase/firestore';
import { createLessonInstancePayload, normalizeLessonInstanceRecord } from './lessonInstancePersistence';
import { canOperateOrganization } from './platformAccess';
import { isRetiredSuperseded } from './scheduleModel';

const LESSONS = 'lessonInstances';
const str = (v) => String(v == null ? '' : v).trim();

// ── deterministic, org-scoped, opaque lesson id from the booking (§11) ──────────
// FNV-1a (two salted rounds → 16 hex). Includes organizationId so a same-named booking id in another org
// can never collide. NEVER derived from student name/email/phone or date+instructor — bookingId is the anchor.
function fnv1a(s) { let h = 0x811c9dc5 >>> 0; for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; } return h >>> 0; }
function hashHex(s) { const a = fnv1a(s); const b = fnv1a(`${s}pass66`); return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0'); }
export function lessonInstanceIdForBooking(organizationId, bookingId) {
  const org = str(organizationId); const bk = str(bookingId);
  if (!org || !bk) return '';
  return `lesson_bk_${hashHex(`${org}::${bk}`)}`;
}

// ── eligibility (§15/§16): only a legitimate, assigned, current client booking hands off ─
const NON_CLIENT = new Set(['travel', 'timeoff', 'staff_activity']);
export function bookingLessonEligibility(booking = {}, { readOnly = false } = {}) {
  const reasons = [];
  const entry = str(booking.scheduleEntryType) || 'client_booking';
  if (entry !== 'client_booking') reasons.push('not_a_client_booking');
  if (NON_CLIENT.has(str(booking.activityType))) reasons.push('non_client_activity');
  if (booking.synthesized) reasons.push('synthesized_source_history');
  if (booking.status === 'canceled' || booking.status === 'cancelled') reasons.push('cancelled');
  if (isRetiredSuperseded(booking)) reasons.push('retired_superseded');
  if (readOnly) reasons.push('historical_read_only'); // caller passes the season read-only flag
  const assigned = !!str(booking.instructorId);
  return { eligible: reasons.length === 0 && assigned, assigned, reasons };
}
export const isBookingLessonEligible = (booking, opts) => bookingLessonEligibility(booking, opts).eligible;

// ── delivery-bearing predicate (§32): an untouched draft is NOT a delivered lesson ──
export function isDeliveryBearing(lesson = {}) {
  if (!lesson) return false;
  if (str(lesson.status) === 'completed') return true;
  const stages = Array.isArray(lesson.stages) ? lesson.stages : [];
  const stageWork = stages.some((s) => (Array.isArray(s.plannedDrillIds) && s.plannedDrillIds.length) || str(s.instructorNotes) || (str(s.completionStatus) && s.completionStatus !== 'not_attempted'));
  const outcome = lesson.lessonOutcome || {};
  const outcomeWork = str(outcome.instructorSummary) || (str(outcome.status) && outcome.status !== 'in_progress');
  return !!(stageWork || outcomeWork);
}

// ── booking → lessonInstance field mapping (§17/§18) ─────────────────────────────
// clientId (commercial) is carried; studentId / clientUserId are NOT fabricated from it — the lesson works
// for an overseas / non-app customer with no Auth participant. sport ← booking discipline (best-effort).
export function buildLessonInstanceFromBooking(booking = {}, { actorInstructorId = '' } = {}) {
  const disc = str(booking.discipline).toLowerCase();
  const sport = disc === 'ski' || disc === 'snowboard' ? disc : (disc.includes('snow') ? 'snowboard' : (disc.includes('ski') ? 'ski' : ''));
  const plan = {
    organizationId: str(booking.organizationId),
    instructorId: str(booking.instructorId),                 // canonical business instructor id (NOT Auth uid)
    instructorAuthUserId: str(booking.instructorAuthUserId), // Auth uid — the field Rules match on
    assignedInstructorId: str(booking.instructorId),
    assignmentSource: 'booking_handoff',
    sport,
    // Pass 66 lineage
    origin: 'booking',
    bookingId: str(booking.id || booking.bookingId),
    bookingRequestId: str(booking.bookingRequestId),
    requestLineId: str(booking.requestLineId),
    requestSlotId: str(booking.requestSlotId),
    clientId: str(booking.clientId),
    scheduledAt: booking.startAt || null,
    serviceDate: str(booking.serviceDate),
    location: str(booking.locationLabel) || str(booking.location) || str(booking.accommodationName),
    groupSize: Number.isFinite(+booking.groupSize) ? Math.floor(+booking.groupSize) : null,
    // §18/§19 — participant Auth identity is NEVER inferred from clientId/requesterUserId here.
    studentId: '',
    studentName: '',
    studentEmail: '',
  };
  return createLessonInstancePayload(plan, { status: 'draft' });
}

// ── authorization (§39): assigned instructor for their own booking, or org owner/admin/ops ─
export const LESSON_HANDOFF_DENIED = 'You do not have permission to start this lesson.';
export function canHandoffLesson(booking = {}, ctx = {}) {
  const org = str(ctx.organizationId || booking.organizationId);
  if (!org) return false;
  if (ctx.userData && canOperateOrganization(ctx.userData, org)) return true; // owner/admin/ops
  const isAssignedInstructor = (str(ctx.actorInstructorId) && str(ctx.actorInstructorId) === str(booking.instructorId))
    || (str(ctx.actorAuthUserId) && str(ctx.actorAuthUserId) === str(booking.instructorAuthUserId));
  return !!isAssignedInstructor;
}

// ── idempotent get-or-create writer (§34) ───────────────────────────────────────
// First call creates ONE record at the deterministic id; retry / concurrent / second-device → 0 duplicates
// (the existing record is returned, its delivery content untouched). Fails closed on auth/eligibility.
export async function getOrCreateLessonInstanceForBooking(db, booking = {}, ctx = {}) {
  const org = str(ctx.organizationId || booking.organizationId);
  const bookingId = str(booking.id || booking.bookingId);
  if (!org || !bookingId) { const e = new Error('Booking and organisation are required.'); e.code = 'LESSON_BOOKING_REQUIRED'; throw e; }
  if (!canHandoffLesson(booking, { ...ctx, organizationId: org })) { const e = new Error(LESSON_HANDOFF_DENIED); e.code = 'LESSON_HANDOFF_DENIED'; throw e; }
  const elig = bookingLessonEligibility(booking, { readOnly: !!ctx.readOnly });
  if (!elig.eligible) {
    const e = new Error(elig.assigned ? `This booking cannot start a lesson (${elig.reasons.join(', ')}).` : 'Assign an instructor before starting the lesson.');
    e.code = elig.assigned ? 'LESSON_NOT_ELIGIBLE' : 'LESSON_UNASSIGNED'; e.reasons = elig.reasons; throw e;
  }

  const lessonId = lessonInstanceIdForBooking(org, bookingId);
  let created = false;
  await runTransaction(db, async (tx) => {
    const ref = doc(db, LESSONS, lessonId);
    const prior = await tx.get(ref);
    if (prior.exists && prior.exists()) return; // §34 idempotent — never rewrite an existing lesson
    tx.set(ref, {
      ...buildLessonInstanceFromBooking(booking, { actorInstructorId: ctx.actorInstructorId }),
      createdBy: str(ctx.actorAuthUserId), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    created = true;
  });
  return { lessonId, created };
}

export { normalizeLessonInstanceRecord };
